import React, { useEffect, useRef } from 'react';
import type { Section } from '../types';

// Seção de tour tipada a partir de Section
type TourSection = Extract<Section, { type: 'tour' }>;

function formatDateLabel(input: string): string {
  // tenta YYYY-MM-DD, DD/MM/YYYY etc.; se falhar, retorna como veio
  const norm = input.replace(/[.]/g, '-').replace(/[\/]/g, '-');
  let d: Date | null = null;
  if (/^\d{4}-\d{1,2}-\d{1,2}$/.test(norm)) {
    d = new Date(norm);
  } else if (/^\d{1,2}-\d{1,2}-\d{4}$/.test(norm)) {
    const [dd, mm, yyyy] = norm.split('-').map(Number);
    d = new Date(yyyy, (mm || 1) - 1, dd || 1);
  }
  if (!d || isNaN(d.getTime())) return input;
  const lbl = d.toLocaleDateString('pt-BR', { day: '2-digit', month: 'short' }).toUpperCase();
  return lbl.replace(/\.$/, ''); // remove ponto final em "AGO." etc.
}

function formatDateParts(input: string): { day: string; mon: string } {
  const norm = input.replace(/[.]/g, '-').replace(/[\/]/g, '-');
  let d: Date | null = null;
  if (/^\d{4}-\d{1,2}-\d{1,2}$/.test(norm)) d = new Date(norm);
  else if (/^\d{1,2}-\d{1,2}-\d{4}$/.test(norm)) {
    const [dd, mm, yyyy] = norm.split('-').map(Number);
    d = new Date(yyyy, (mm || 1) - 1, dd || 1);
  }
  if (!d || isNaN(d.getTime())) return { day: input, mon: '' };
  const day = d.toLocaleDateString('pt-BR', { day: '2-digit' });
  const mon = d
    .toLocaleDateString('pt-BR', { month: 'short' })
    .replace('.', '')
    .toUpperCase();
  return { day, mon };
}

export default function Schedule({ section }: { section: TourSection }) {
  const { gallery = [], items } = section;
  const scrollerRef = useRef<HTMLDivElement>(null);

  // autoplay leve do carrossel (sem setas)
  useEffect(() => {
    const wrap = scrollerRef.current;
    if (!wrap || gallery.length < 2) return;
    let idx = 0;
    const tick = () => {
      idx = (idx + 1) % gallery.length;
      const x = idx * wrap.clientWidth;
      wrap.scrollTo({ left: x, behavior: 'smooth' });
    };
    const id = window.setInterval(tick, 4500);
    return () => window.clearInterval(id);
  }, [gallery]);

  return (
    <section id="tour" className="kl-section kl-tour" aria-label="Tour schedule">
      {section.title && (
        <header className="kl-sec-head">
          <h2 className="kl-sec-title">{section.title}</h2>
        </header>
      )}
      {/* Galeria no topo (scroll-snap; swipe nativo; sem botões) */}
      {gallery.length > 0 && (
        <div ref={scrollerRef} className="kl-tour-gallery">
          {gallery.map((src, i) => (
            <img key={i} src={src} alt={`tour-slide-${i + 1}`} />
          ))}
        </div>
      )}

      {/* Lista de datas: DATA  FESTA – CIDADE  [GET TICKETS] */}
      <div className="kl-tour-list">
        {items.map((it, i) => {
          const p = formatDateParts(it.date);
          return (
            <div className="kl-tour-item" key={`${it.date}-${it.party}-${i}`}>
              <div className="kl-date">
                <span className="kl-date-day">{p.day}</span>
                <span className="kl-date-mon">{p.mon}</span>
              </div>
              <div className="kl-tour-headline">
                <span className="kl-tour-party">{it.party}</span>
                <span className="kl-tour-sep"> – </span>
                <span className="kl-tour-city">{it.city}</span>
              </div>
              {it.link ? (
                <a className="kl-tour-link" href={it.link} target="_blank" rel="noreferrer">
                  COMPRAR
                </a>
              ) : (
                <span />
              )}
            </div>
          );
        })}
      </div>
    </section>
  );
}
